import React, { useEffect, useRef, useState } from 'react';
import 'twin.macro';
import { Subrow } from './Subrow';
import { Color } from '@teetsh/app/src/types/colors';
import { Item } from '@teetsh/app/src/types/programmations';
import tw from 'twin.macro';
import { CenterRotatedCell } from '@teetsh/app/src/components/CenterRotatedCell';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import _isEmpty from 'lodash-es/isEmpty';
import { subtitleStyle } from '../styles';
import { RowTitleOrientation } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/types';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';
import { BreakBeforePageDivider } from '../../Preview/BreakBeforePageDivider';

export type Header = {
  name: string;
  color: Color | TagColorWithIntensity;
  details?: string;
};
export type ISubrow = {
  header?: Header;
  blocks: ItemBlock[];
};
export type ItemBlock = {
  items: Item[];
};
export type IRow = {
  header: Header;
  subrows: ISubrow[];
};

interface Props {
  header: Header;
  subrows: ISubrow[];
  hasFirstColSubtitles?: boolean;
  rowTitleOrientation: RowTitleOrientation;
  preventCutting: boolean;
  breakBefore: boolean;
}

export function Row(props: Props) {
  const {
    header,
    subrows,
    hasFirstColSubtitles,
    rowTitleOrientation,
    preventCutting,
    breakBefore,
  } = props;
  const cellRef = useRef<HTMLDivElement>(null);
  const [cellWidth, setCellWidth] = useState('auto');

  const missingName = _isEmpty((header.name || '').trim());
  const missingSubtitle = !header.details && hasFirstColSubtitles;

  // We force a recompute on firefox because it doesn't get the width right
  // https://www.notion.so/teetsh/Probl-me-firefox-writing-mode-flex-2d2d272cd34e455c94ba584a992f25a1
  useEffect(() => {
    if (cellRef.current) {
      const width = cellRef.current.getBoundingClientRect().width;
      if (width === 0) {
        // Handling Firefox's issue with writing-mode
        setCellWidth('max-content'); // Fallback width if no width is detected
      } else {
        setCellWidth(`${width}px`);
      }
    }
  }, [header, hasFirstColSubtitles]); // Rerun when header or subtitle settings change

  return (
    <div
      css={[
        preventCutting && tw`break-inside-avoid`,
        breakBefore &&
          tw`break-before-page first:break-before-avoid first:[&>div]:first:hidden`,
      ]}
    >
      {breakBefore && <BreakBeforePageDivider tw="mb-4" />}
      {rowTitleOrientation === 'horizontal' && (
        <div
          tw="flex items-baseline gap-x-2 mb-2 print:break-after-avoid"
          css={[
            tw`flex-grow border-r border-neutral-quiet bg-white px-4 py-1 rounded-lg text-sm font-semibold`,
            bgColors(header.color),
            getTextColorFromBackgroundColor(header.color),
          ]}
        >
          <div>{header.name}</div>
          {(header.details || missingSubtitle) && (
            <span css={[subtitleStyle]}>{header.details}</span>
          )}
        </div>
      )}
      <div tw="flex">
        {rowTitleOrientation === 'vertical' && (
          <div tw="mr-2">
            <div
              ref={cellRef}
              style={{ width: cellWidth }}
              css={[
                tw`flex items-center justify-center h-full font-medium whitespace-nowrap py-4 rounded-lg text-base`,
                bgColors(header.color),
                getTextColorFromBackgroundColor(header.color),
              ]}
            >
              <CenterRotatedCell active>
                <div tw="flex flex-col items-center">
                  <div
                    css={[(missingName || missingSubtitle) && tw`invisible`]}
                  >
                    {missingName ? 'A' : header.name}
                  </div>
                  {(header.details || missingSubtitle) && (
                    <span
                      css={[subtitleStyle, missingSubtitle && tw`invisible`]}
                    >
                      {header.details || 'A'}
                    </span>
                  )}
                  {!missingName && missingSubtitle && (
                    <div tw="absolute w-full flex items-center">
                      {header.name}
                    </div>
                  )}
                </div>
              </CenterRotatedCell>
            </div>
          </div>
        )}
        <div tw="flex flex-1 flex-col gap-y-2">
          {subrows.map((subrow, idx) => (
            // Cette div est importante sinon ça baiiize le PDF
            // On dirait que chrome print n'aime pas trop les flex de flex
            <div key={idx}>
              <Subrow
                header={subrows.length > 1 ? subrow.header : undefined}
                blocks={subrow.blocks}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
